/**
 * Parse KYC_TIER_REQUIRED from API errors thrown by apiClient or apiService.
 *
 * The backend (middleware/kycMiddleware.js requireActiveWalletAtTier) returns:
 *   { success:false, errorCode:'KYC_TIER_REQUIRED', data:{ requiredTier, currentTier, kycStatus, action } }
 *
 * apiClient attaches .errorCode on the thrown Error; apiService throws ApiError
 * with .payload. This helper normalises both.
 */
export interface KycTierErrorDetails {
  requiredTier: number;
  currentTier: number;
  kycStatus: string;
  action: string;
  message: string;
}

function asObj(v: unknown): Record<string, unknown> | undefined {
  return v && typeof v === 'object' ? (v as Record<string, unknown>) : undefined;
}

export function parseKycTierError(err: unknown): KycTierErrorDetails | null {
  const anyErr = asObj(err);
  if (!anyErr) return null;

  const payload = asObj(anyErr.payload);
  const responseData = asObj(asObj(anyErr.response)?.data);
  const directData = asObj(anyErr.data);

  const errorCode =
    (anyErr.errorCode as string | undefined) ||
    (payload?.errorCode as string | undefined) ||
    (responseData?.errorCode as string | undefined);

  if (errorCode !== 'KYC_TIER_REQUIRED') return null;

  // KYC details live under .data on the backend response body.
  const details =
    asObj(payload?.data) ||
    asObj(responseData?.data) ||
    directData ||
    {};

  const message =
    (anyErr.message as string | undefined) ||
    (payload?.message as string | undefined) ||
    'Complete ID verification (KYC) to use your wallet balance.';

  return {
    requiredTier: Number(details.requiredTier ?? 1),
    currentTier: Number(details.currentTier ?? 0),
    kycStatus: String(details.kycStatus ?? 'not_started'),
    action: String(details.action ?? 'open_kyc'),
    message,
  };
}
